/**
 * 安全解析 JSON，将超过 JS 安全整数范围的数字转为字符串
 * 避免 Snowflake ID 等大整数在 JSON.parse 时精度丢失
 *
 * 例如：7437625881213800448 → "7437625881213800448"（保持字符串）
 * 而不是被截断为 7437625881213800000
 *
 * 处理场景：
 * - 对象值: {"id": 7437625881213800448} → {"id": "7437625881213800448"}
 * - 数组元素: [7437625881213800448] → ["7437625881213800448"]
 * - 不影响字符串内容: {"desc": "ID: 7437625881213800448"} 保持不变
 */
export function safeJsonParse(jsonStr: unknown): any {
  // 容忍「已经解析过」的值：API 层 (api/axios-config.ts 的 transformResponse) 已经对
  // 整个响应体跑过一次 safeJsonParse，所以像 activity.eventPayload 这种 JSONB 嵌套字段
  // 到达调用方时已经是对象/数组（大整数 Snowflake ID 也已在那一层转成字符串）。再对一个
  // 非字符串调用本函数会在 `jsonStr.replace(...)` 处抛 TypeError，导致调用方 catch 成 {}，
  // 把整个 payload 静默丢掉。这里直接原样返回非字符串；nullish → {}。
  if (typeof jsonStr !== 'string') {
    return jsonStr ?? {}
  }
  // 先移除字符串值以避免误匹配，替换后再还原
  // 策略：用占位符替换所有 JSON 字符串值，对剩余内容做数字替换，再还原字符串
  const strings: string[] = []
  // 匹配 JSON 字符串（处理转义引号）
  const stripped = jsonStr.replace(/"(?:[^"\\]|\\.)*"/g, (match) => {
    strings.push(match)
    return `"__SAFE_JSON_STR_${strings.length - 1}__"`
  })

  // 在去除字符串后的内容中，替换所有超过安全范围的纯整数
  // 匹配场景：冒号后的值、数组元素、顶层值
  const safed = stripped.replace(/(?<=[:,\[\s])(\d{16,})(?=\s*[,\]\}])|(?<=:\s*)(\d{16,})(?=\s*[,\}])/g, (match) => {
    if (Number(match) > Number.MAX_SAFE_INTEGER) {
      return `"${match}"`
    }
    return match
  })

  // 还原字符串占位符
  const restored = safed.replace(/"__SAFE_JSON_STR_(\d+)__"/g, (_, idx) => {
    return strings[Number(idx)]
  })

  return JSON.parse(restored)
}
